import { TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';
import { bbox } from '@turf/turf';
import * as _ from 'lodash';
import qs from 'query-string';

import renderSettings from '@src/containers/Viewer/subComponents/RenderRaster/options/renderSettings.json';
import {
  setIsMeasure,
  setMeasureJson,
  setPrevMeasureJson,
  setPrevSelectedFeatureIndexes,
  setSelectedFeatureIndexes
} from '@src/redux/_reducers/mapControl';
import {
  setLeftColorScale,
  setLeftUrl,
  setRightColorScale,
  setRightUrl
} from '@src/redux/_reducers/projectReducer';
import store from '@src/redux/store';

import { RenderOptions } from './IndiceUtils';
import { ReactTileLayer } from './MapUtils';
import { getUSTimeFormat } from './dateUtils';
import { DarkThemeMapStyle } from './maps/styles';

/**
 *
 * @param {String} side  - `left` or `right` as string to render on a side
 * @param {String} style_url - mapbox style id url or TiTiler stringified params
 * @param {*} misc - pass other strings to make it further unique
 * @returns
 */
const generateTileID = (side, style_url, misc = '') => {
  const id = `${side}-side-map-${style_url}${misc}`;
  return id;
};

export function GenerateCustomIndiceURL(formula, side) {
  const renderParams = {
    asset_as_band: true,
    resampling_method: renderSettings.resampling_method,
    expression: formula,
    colormap_name: 'spectral'
  };
  let url = '&' + qs.stringify(renderParams, { ...RenderOptions });

  if (side === 'right') {
    store.dispatch(setRightUrl(url));
    store.dispatch(setRightColorScale('custom'));
    return;
  }
  if (side === 'left') {
    store.dispatch(setLeftUrl(url));
    store.dispatch(setLeftColorScale('custom'));
    return;
  }
}

export function GenerateBands(band, setSelectedBand, side) {
  const state = store.getState();
  const activeAoi = state.projects.activeAoi;

  if (setSelectedBand !== null) {
    setSelectedBand(band);
  }
  const renderParams = {
    asset_as_band: true,
    resampling_method: renderSettings.resampling_method,
    assets: band,
    rescale: `${activeAoi.metadata.rescale[0]}%2C${activeAoi.metadata.rescale[1]}`,
    nodata: 0
  };
  let url = '&' + qs.stringify(renderParams, { ...RenderOptions });

  if (side === 'right') {
    store.dispatch(setRightUrl(url));
    store.dispatch(setRightColorScale('bands'));
    return;
  }

  if (side === 'left') {
    store.dispatch(setLeftUrl(url));
    store.dispatch(setLeftColorScale('bands'));
    return;
  }
}

export function pushBaseMap(side, style = DarkThemeMapStyle) {
  const id = generateTileID(side, style.id);
  return new TileLayer({
    id: id,
    data: `https://api.mapbox.com/styles/v1/${
      style.uri
    }/tiles/256/{z}/{x}/{y}@2x?access_token=${
      import.meta.env.VITE_MAPBOX_TOKEN
    }`,
    minZoom: 0,
    maxZoom: 19,
    tileSize: 256,
    renderSubLayers: props => {
      const {
        bbox: { west, south, east, north }
      } = props.tile;
      return new BitmapLayer(props, {
        data: null,
        image: props.data,
        bounds: [west, south, east, north]
      });
    },
    onTileError: () => {
      return null;
    }
  });
}

export function renderVizLayers(layers) {
  const state = store.getState();
  const activeAoi = state.projects.activeAoi;
  const splitConfig = state.projects.visualize.splitConfig;
  const format = state.sourceActions.tileRenderFormat;

  const aoi_bounds = _.has(activeAoi, 'geometry')
    ? [...bbox(activeAoi.geometry)]
    : [];

  let isLeftPreview = splitConfig.left.leftPreview.length > 0;
  let isRightPreview = splitConfig.right.rightPreview.length > 0;

  let isLeft = false;
  let isRight = false;

  const addLayer = ({ side, url, opacity = [1], isReactNode = false }) => {
    const id = generateTileID(side, url);
    layers.push(ReactTileLayer(id, id, url, aoi_bounds, isReactNode, opacity));
  };

  if (
    splitConfig.left.leftPreview.startsWith(
      `${import.meta.env.VITE_COG_TILER}`
    ) ||
    splitConfig.left.leftPreview.startsWith(
      `${import.meta.env.VITE_STAC_TILER}`
    )
  ) {
    addLayer({ side: 'left', url: splitConfig.left.leftPreview });
    isLeft = true;
  }

  if (
    splitConfig.right.rightPreview.startsWith(
      `${import.meta.env.VITE_COG_TILER}`
    ) ||
    splitConfig.right.rightPreview.startsWith(
      `${import.meta.env.VITE_STAC_TILER}`
    )
  ) {
    addLayer({ side: 'right', url: splitConfig.right.rightPreview });
    isRight = true;
  }

  splitConfig?.left?.imageLayers
    ?.filter(item => item.visible)
    ?.forEach(item => {
      if (item.url.startsWith(`${import.meta.env.VITE_COG_TILER}`)) {
        addLayer({ side: 'left', url: item.url, opacity: item.opacity });
      }
    });

  splitConfig?.right?.imageLayers
    ?.filter(item => item.visible)
    ?.forEach(item => {
      if (item.url.startsWith(`${import.meta.env.VITE_COG_TILER}`)) {
        addLayer({ side: 'right', url: item.url, opacity: item.opacity });
      }
    });

  if (activeAoi?.images) {
    Object.keys(activeAoi.images).map(date => {
      // Left side
      if (
        isLeftPreview &&
        !isLeft &&
        getUSTimeFormat(date) === splitConfig.left.leftDate
      ) {
        activeAoi.images[date].map((image, index) => {
          const tilerURL = `${
            import.meta.env.VITE_STAC_TILER
          }/stac/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?url=${
            image.url
          }${splitConfig.left.leftPreview}`;
          const id = generateTileID(
            'left',
            splitConfig.left.leftPreview,
            `-${date}-${index}`
          );
          layers.push(ReactTileLayer(id, id, tilerURL, aoi_bounds, false));
        });
      }

      // Right side
      if (
        isRightPreview &&
        !isRight &&
        getUSTimeFormat(date) === splitConfig.right.rightDate
      ) {
        activeAoi.images[date].map((image, index) => {
          const tilerURL = `${
            import.meta.env.VITE_STAC_TILER
          }/stac/tiles/WebMercatorQuad/{z}/{x}/{y}.${format}?url=${
            image.url
          }${splitConfig.right.rightPreview}`;
          const id = generateTileID(
            'right',
            splitConfig.right.rightPreview,
            `-${date}-${index}`
          );
          layers.push(ReactTileLayer(id, id, tilerURL, aoi_bounds, false));
        });
      }
    });
  }
}

export function triggerMeasurement(
  boolean,
  measureJson = { type: 'FeatureCollection', features: [] },
  selectedFeatureIndexes = []
) {
  store.dispatch(setIsMeasure(boolean));
  store.dispatch(setPrevMeasureJson(measureJson));
  store.dispatch(
    setMeasureJson({
      type: 'FeatureCollection',
      features: []
    })
  );
  store.dispatch(setPrevSelectedFeatureIndexes(selectedFeatureIndexes));
  store.dispatch(setSelectedFeatureIndexes([]));
}

export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        const { latitude, longitude } = position.coords;
        resolve([latitude, longitude]);
        return;
      }, reject);
    } else {
      reject(
        'Your browser does not support location service! Cannot intent to your current location. Please update your browser to latest version!'
      );
    }
  });
}
